import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { 
  LayoutDashboard, 
  Package, 
  DollarSign, 
  FileText, 
  ClipboardList,
  Users,
  Car,
  Settings,
  Wrench,
  LogOut,
  UserCog,
  ShieldCheck,
  History,
  Building2,
  FileSignature,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  Calculator,
  Wallet,
  TrendingUp,
  AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const menuItems = [
  { to: '/oficina/dashboard', icon: LayoutDashboard, label: 'Dashboard', perm: 'dashboard:view' },
  { to: '/oficina/estoque', icon: Package, label: 'Estoque', perm: 'estoque:view' },
  { 
    icon: DollarSign, 
    label: 'Financeiro',
    submenu: [
      { to: '/oficina/financeiro', icon: TrendingUp, label: 'Fluxo de Caixa', perm: 'financeiro:view' },
      { to: '/oficina/custos', icon: Wallet, label: 'Custos', perm: 'custos:view' },
      { to: '/oficina/contabilidade', icon: Calculator, label: 'Contabilidade', perm: 'contabilidade:view' },
    ]
  },
  { 
    icon: Wrench, 
    label: 'Serviços',
    submenu: [
      { to: '/oficina/orcamentos', icon: FileText, label: 'Orçamentos', perm: 'orcamentos:view' },
      { to: '/oficina/ordens', icon: ClipboardList, label: 'Ordens', perm: 'ordens:view' },
      { to: '/oficina/nfe', icon: FileSignature, label: 'Notas Fiscais', perm: 'financeiro:view' },
    ]
  },
  { 
    icon: Users, 
    label: 'Cadastro',
    submenu: [
      { to: '/oficina/clientes', icon: Users, label: 'Clientes', perm: 'clientes:view' },
      { to: '/oficina/veiculos', icon: Car, label: 'Veículos', perm: 'veiculos:view' },
    ]
  },
  { to: '/oficina/configuracoes', icon: Settings, label: 'Configurações', perm: 'configuracoes:view' },
];

export const Layout = () => {
  const [configOficina, setConfigOficina] = useState({ nome_oficina: 'AutoFlow Pro', logo_url: '' });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const { user, logout, isAdmin, hasPerm, cargoNome, empresaAtiva, empresasDisponiveis, trocarEmpresa } = useAuth();
  const [certWarning, setCertWarning] = useState(null);

  // Buscar status do certificado da empresa ativa (alerta vencimento)
  useEffect(() => {
    const valido_ate = empresaAtiva?.certificado_valido_ate;
    if (!valido_ate) {
      setCertWarning(null);
      return;
    }
    const dias = Math.floor((new Date(valido_ate) - new Date()) / (1000 * 60 * 60 * 24));
    if (dias <= 30) {
      setCertWarning({ dias, vencido: dias < 0 });
    } else {
      setCertWarning(null);
    }
  }, [empresaAtiva]);
  const navigate = useNavigate();
  const location = useLocation();

  // Verificar rotas para manter submenus abertos
  const financeiroRoutes = ['/oficina/financeiro', '/oficina/custos', '/oficina/contabilidade'];
  const cadastroRoutes = ['/oficina/clientes', '/oficina/veiculos'];
  const servicosRoutes = ['/oficina/orcamentos', '/oficina/ordens'];

  useEffect(() => {
    if (financeiroRoutes.includes(location.pathname)) {
      setOpenMenus(prev => ({ ...prev, Financeiro: true }));
    }
    if (cadastroRoutes.includes(location.pathname)) {
      setOpenMenus(prev => ({ ...prev, Cadastro: true }));
    }
    if (servicosRoutes.includes(location.pathname)) {
      setOpenMenus(prev => ({ ...prev, Serviços: true }));
    }
  }, [location.pathname]);

  const toggleMenu = (label) => {
    setOpenMenus(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const isMenuActive = (submenu) => {
    return submenu.some(item => location.pathname === item.to);
  };

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const { data } = await api.get('/configuracoes/oficina');
        setConfigOficina(data);
      } catch (error) {
        console.error('Erro ao carregar configurações:', error);
      }
    };
    loadConfig();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Filtrar menus por permissões
  const visibleMenuItems = menuItems
    .map((item) => {
      if (item.submenu) {
        const visibleSub = item.submenu.filter((s) => hasPerm(s.perm));
        return visibleSub.length > 0 ? { ...item, submenu: visibleSub } : null;
      }
      return hasPerm(item.perm) ? item : null;
    })
    .filter(Boolean);

  return (
    <div className="flex h-screen bg-obsidian" data-testid="layout-container">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-carbon border-b border-white/5 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {configOficina.logo_url ? (
            <img src={configOficina.logo_url} alt="Logo" className="w-8 h-8 object-contain rounded-sm" />
          ) : (
            <div className="w-8 h-8 bg-electric-blue rounded-sm flex items-center justify-center">
              <Wrench className="w-4 h-4 text-white" />
            </div>
          )}
          <h1 className="text-sm font-bold text-white uppercase tracking-tight truncate max-w-[150px]">
            {configOficina.nome_oficina}
          </h1>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="text-white p-2"
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={closeMobileMenu}
        />
      )}

      {/* Sidebar - Desktop sempre visível, Mobile como drawer */}
      <aside 
        className={`
          fixed lg:relative inset-y-0 left-0 z-50
          w-64 bg-carbon border-r border-white/5 flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        data-testid="sidebar"
      >
        {/* Logo - Desktop only */}
        <div className="hidden lg:block p-6 border-b border-white/5">
          <div className="flex items-center space-x-3">
            {configOficina.logo_url ? (
              <img src={configOficina.logo_url} alt="Logo" className="w-10 h-10 object-contain rounded-sm" />
            ) : (
              <div className="w-10 h-10 bg-electric-blue rounded-sm flex items-center justify-center">
                <Wrench className="w-6 h-6 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-xl font-bold text-white uppercase tracking-tight">{configOficina.nome_oficina}</h1>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Pro</p>
            </div>
          </div>
        </div>

        {/* Mobile Logo spacer */}
        <div className="lg:hidden h-14" />
        
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
          {visibleMenuItems.map((item) => (
            item.submenu ? (
              // Menu com submenu
              <div key={item.label}>
                <button
                  onClick={() => toggleMenu(item.label)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-sm text-slate-300 hover:text-white hover:bg-white/5 ${
                    isMenuActive(item.submenu) ? 'text-white bg-white/5' : ''
                  }`}
                  data-testid={`nav-${item.label.toLowerCase()}-toggle`}
                >
                  <div className="flex items-center space-x-3">
                    <item.icon className="w-5 h-5" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </div>
                  {openMenus[item.label] ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                
                {/* Submenu */}
                {openMenus[item.label] && (
                  <div className="ml-4 mt-1 space-y-1 border-l border-white/10 pl-2">
                    {item.submenu.map((subitem) => (
                      <NavLink
                        key={subitem.to}
                        to={subitem.to}
                        onClick={closeMobileMenu}
                        data-testid={`nav-${subitem.label.toLowerCase().replace(/ /g, '-')}`}
                        className={({ isActive }) =>
                          `sidebar-nav-item flex items-center justify-between space-x-3 px-3 py-2 rounded-sm text-slate-400 hover:text-white text-sm ${
                            isActive ? 'active text-electric-blue bg-electric-blue/10' : ''
                          }`
                        }
                      >
                        <div className="flex items-center space-x-3">
                          <subitem.icon className="w-4 h-4" />
                          <span className="text-sm">{subitem.label}</span>
                        </div>
                        {subitem.to === '/oficina/nfe' && certWarning && (
                          <span
                            className={`flex items-center text-[10px] px-1.5 py-0.5 rounded font-medium ${
                              certWarning.vencido ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'
                            }`}
                            title={certWarning.vencido ? 'Certificado vencido' : `Certificado vence em ${certWarning.dias} dia(s)`}
                            data-testid="cert-expiring-badge"
                          >
                            <AlertTriangle className="w-3 h-3 mr-0.5" />
                            {certWarning.vencido ? 'Vencido' : `${certWarning.dias}d`}
                          </span>
                        )}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              // Menu normal
              <NavLink
                key={item.to}
                to={item.to}
                onClick={closeMobileMenu}
                data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                className={({ isActive }) =>
                  `sidebar-nav-item flex items-center space-x-3 px-4 py-3 rounded-sm text-slate-300 hover:text-white active:bg-white/10 ${
                    isActive ? 'active text-white bg-white/5' : ''
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                <span className="text-sm font-medium">{item.label}</span>
              </NavLink>
            )
          ))}
          
          {/* Link de Usuários - apenas para admin */}
          {isAdmin() && (
            <>
              <NavLink
                to="/oficina/usuarios"
                onClick={closeMobileMenu}
                data-testid="nav-usuarios"
                className={({ isActive }) =>
                  `sidebar-nav-item flex items-center space-x-3 px-4 py-3 rounded-sm text-slate-300 hover:text-white active:bg-white/10 ${
                    isActive ? 'active text-white bg-white/5' : ''
                  }`
                }
              >
                <UserCog className="w-5 h-5" />
                <span className="text-sm font-medium">Usuários</span>
              </NavLink>
              <NavLink
                to="/oficina/cargos"
                onClick={closeMobileMenu}
                data-testid="nav-cargos"
                className={({ isActive }) =>
                  `sidebar-nav-item flex items-center space-x-3 px-4 py-3 rounded-sm text-slate-300 hover:text-white active:bg-white/10 ${
                    isActive ? 'active text-white bg-white/5' : ''
                  }`
                }
              >
                <ShieldCheck className="w-5 h-5" />
                <span className="text-sm font-medium">Cargos</span>
              </NavLink>
              <NavLink
                to="/oficina/auditoria"
                onClick={closeMobileMenu}
                data-testid="nav-auditoria"
                className={({ isActive }) =>
                  `sidebar-nav-item flex items-center space-x-3 px-4 py-3 rounded-sm text-slate-300 hover:text-white active:bg-white/10 ${
                    isActive ? 'active text-white bg-white/5' : ''
                  }`
                }
              >
                <History className="w-5 h-5" />
                <span className="text-sm font-medium">Auditoria</span>
              </NavLink>
            </>
          )}
        </nav>
        
        {/* Empresa Ativa + User Menu */}
        <div className="p-4 border-t border-white/5 space-y-2">
          {empresaAtiva && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-between text-slate-300 hover:text-white hover:bg-white/5 px-2 h-auto py-2"
                  data-testid="empresa-switcher"
                  disabled={empresasDisponiveis.length <= 1}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Building2 className="w-4 h-4 text-electric-blue flex-shrink-0" />
                    <div className="text-left min-w-0">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500">Empresa</p>
                      <p className="text-xs font-medium text-white truncate max-w-[140px]" title={empresaAtiva.nome}>
                        {empresaAtiva.nome}
                      </p>
                    </div>
                  </div>
                  {empresasDisponiveis.length > 1 && <ChevronDown className="w-3 h-3 flex-shrink-0" />}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64 bg-carbon border-white/10 text-white" align="start">
                <div className="px-2 py-1.5">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Trocar empresa</p>
                </div>
                <DropdownMenuSeparator className="bg-white/10" />
                {empresasDisponiveis.map((emp) => (
                  <DropdownMenuItem
                    key={emp.id}
                    onClick={async () => {
                      if (emp.id !== empresaAtiva.id) {
                        const res = await trocarEmpresa(emp.id);
                        if (res.success) window.location.reload();
                      }
                    }}
                    className={`cursor-pointer ${emp.id === empresaAtiva.id ? 'text-electric-blue bg-electric-blue/5' : 'text-slate-300'}`}
                    data-testid={`empresa-option-${emp.id}`}
                  >
                    <Building2 className="w-3.5 h-3.5 mr-2" />
                    <span className="truncate">{emp.nome}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-between text-slate-300 hover:text-white hover:bg-white/5"
                data-testid="user-menu-trigger"
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isAdmin() ? 'bg-amber-500/20 text-amber-500' : 'bg-electric-blue/20 text-electric-blue'
                  }`}>
                    {user?.nome?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-white truncate max-w-[100px]">{user?.nome}</p>
                    <p className="text-xs text-slate-500">{isAdmin() ? 'Admin' : (cargoNome || 'Sem cargo')}</p>
                  </div>
                </div>
                <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 bg-carbon border-white/10 text-white" align="end">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.nome}</p>
                <p className="text-xs text-slate-500">@{user?.username}</p>
              </div>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="text-red-400 focus:text-red-400 focus:bg-red-500/10 cursor-pointer"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pt-14 lg:pt-0" data-testid="main-content">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
